import { useEffect, useMemo, useState } from 'react';
import { marketingGbpAPI } from '../../api/api';
import {
  Building2, Star, RefreshCw, Phone, MapPin, Globe, Clock,
  MessageSquare, Sparkles, ExternalLink, Copy, AlertTriangle,
  CheckCircle2, TrendingUp, Users, Edit, HelpCircle, Trash2, Send, Plus,
  BarChart3, Eye, MousePointerClick, Navigation,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';

/**
 * /marketing/google-business/ — Ficha pública de Duetto en Google Maps.
 *
 * Fase MVP (Places API): ficha + reviews + KPIs + alertas Telegram.
 * Fase 2 (Business Profile API, cuando Google apruebe): insights, posts,
 * responder reseñas desde el ERP.
 */

const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString('es-ES'));

function StarsRating({ rating, size = 'sm' }) {
  if (rating == null) return <span className="text-gray-400">—</span>;
  const full = Math.floor(rating);
  const has_half = rating - full >= 0.5;
  const cls = size === 'lg' ? 'w-5 h-5' : 'w-3.5 h-3.5';
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${cls} ${
            i <= full
              ? 'fill-yellow-400 text-yellow-400'
              : i === full + 1 && has_half
              ? 'fill-yellow-400 text-yellow-400 opacity-50'
              : 'text-gray-300'
          }`}
        />
      ))}
      <span className={`ml-1.5 font-semibold ${size === 'lg' ? 'text-lg' : 'text-sm'}`}>
        {rating?.toFixed(1)}
      </span>
    </div>
  );
}

function DistribucionRating({ distribucion, total }) {
  if (!distribucion || !total) return null;
  return (
    <div className="space-y-1">
      {[5, 4, 3, 2, 1].map((star) => {
        const n = distribucion[star] || 0;
        const pct = total ? Math.round((n / total) * 100) : 0;
        return (
          <div key={star} className="flex items-center gap-2 text-xs">
            <span className="w-4 text-gray-600">{star}★</span>
            <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
              <div
                className={`h-2 rounded-full ${
                  star >= 4 ? 'bg-emerald-500' : star === 3 ? 'bg-amber-500' : 'bg-red-500'
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="w-12 text-right text-gray-700">{n} ({pct}%)</span>
          </div>
        );
      })}
    </div>
  );
}

function ReviewCard({ r, onSugerirRespuesta }) {
  const ratingColor =
    r.rating >= 5 ? 'border-emerald-300 bg-emerald-50/30' :
    r.rating >= 4 ? 'border-blue-300 bg-blue-50/30' :
    r.rating >= 3 ? 'border-amber-300 bg-amber-50/30' :
    'border-red-300 bg-red-50/30';
  return (
    <div className={`border rounded-lg p-3 ${ratingColor}`}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          {r.author_photo && (
            <img src={r.author_photo} alt={r.author_name || 'autor'}
              className="w-7 h-7 rounded-full border" />
          )}
          <div className="min-w-0">
            <div className="font-medium text-sm truncate">{r.author_name || 'Anónimo'}</div>
            <div className="text-[11px] text-gray-500">
              {r.relative_publish_time || (r.publish_time && new Date(r.publish_time).toLocaleDateString('es-ES'))}
            </div>
          </div>
        </div>
        <StarsRating rating={r.rating} />
      </div>
      {r.text && (
        <div className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">
          {r.text}
        </div>
      )}
      <div className="flex items-center gap-2 mt-2.5 text-[11px]">
        <button
          onClick={() => onSugerirRespuesta(r)}
          className="text-purple-700 hover:underline inline-flex items-center gap-1"
        >
          <Sparkles className="w-3 h-3" /> Sugerir respuesta IA
        </button>
        {r.google_maps_uri && (
          <a href={r.google_maps_uri} target="_blank" rel="noopener noreferrer"
            className="text-gray-500 hover:text-gray-700 inline-flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> Ver en Maps
          </a>
        )}
      </div>
    </div>
  );
}

function SugerenciaRespuestaDialog({ open, review, onClose }) {
  const [loading, setLoading] = useState(false);
  const [borradores, setBorradores] = useState([]);
  const [tono, setTono] = useState('');
  const [error, setError] = useState(null);
  const [copiado, setCopiado] = useState(null);

  useEffect(() => {
    if (!open || !review) {
      setBorradores([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    marketingGbpAPI.sugerirRespuesta(review.review_id, review.place_id)
      .then((res) => {
        if (!res.data?.ok) {
          setError(res.data?.error || 'Error generando borradores');
          setBorradores([]);
        } else {
          setBorradores(res.data.borradores || []);
          setTono(res.data.tono_aplicado || '');
        }
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Error en IA'))
      .finally(() => setLoading(false));
  }, [open, review]);

  const copiar = async (texto, idx) => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(idx);
      setTimeout(() => setCopiado(null), 2000);
    } catch (e) {
      setError('No se pudo copiar');
    }
  };

  if (!review) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Borradores de respuesta IA
          </DialogTitle>
          <DialogDescription>
            Tono aplicado: <strong>{tono || '…'}</strong>. Las respuestas se generan
            con Gemini. Cópialas y pégalas en business.google.com para responder.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-gray-50 border rounded-md p-3 text-sm">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold">{review.author_name}</span>
            <StarsRating rating={review.rating} />
          </div>
          <div className="text-gray-700 whitespace-pre-wrap text-xs">
            {review.text || <em className="text-gray-400">(sin texto)</em>}
          </div>
        </div>

        {loading && (
          <div className="py-8 text-center text-sm text-gray-500">
            <RefreshCw className="w-5 h-5 mx-auto animate-spin mb-2" />
            Generando borradores…
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800 mt-2">
            {error}
          </div>
        )}
        {!loading && !error && borradores.length === 0 && (
          <div className="text-sm text-gray-500 py-4 text-center">Sin borradores generados.</div>
        )}
        {!loading && borradores.map((b, i) => (
          <div key={i} className="border border-purple-200 bg-purple-50/40 rounded-md p-3 mt-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] uppercase font-semibold text-purple-700">
                Borrador {i + 1} · {b.split(/\s+/).length} palabras
              </span>
              <button
                onClick={() => copiar(b, i)}
                className="text-xs text-gray-700 hover:text-gray-900 inline-flex items-center gap-1"
              >
                {copiado === i ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                {copiado === i ? '¡Copiado!' : 'Copiar'}
              </button>
            </div>
            <div className="text-sm text-gray-800 whitespace-pre-wrap">{b}</div>
          </div>
        ))}

        <DialogFooter className="mt-2">
          <a
            href="https://business.google.com/reviews"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 inline-flex items-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Abrir Business Profile para responder
          </a>
          <button onClick={onClose}
            className="px-3 py-2 text-sm bg-duetto-primary text-white rounded-md hover:opacity-90">
            Cerrar
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MarketingGoogleBusiness() {
  const [data, setData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [filtroRating, setFiltroRating] = useState('');
  const [iaReview, setIaReview] = useState(null);
  // Fase 2: tabs + estado configuración
  const [tabActiva, setTabActiva] = useState('reviews');
  const [estadoFase2, setEstadoFase2] = useState(null); // {configurado, instrucciones}

  const cargar = async () => {
    setLoading(true);
    setError(null);
    try {
      const [resFicha, resReviews, resFase2] = await Promise.all([
        marketingGbpAPI.ficha(),
        marketingGbpAPI.listarReviews({ limit: 100 }),
        marketingGbpAPI.estadoFase2().catch(() => ({ data: { configurado: false } })),
      ]);
      setData(resFicha.data);
      setReviews(resReviews.data?.items || []);
      setEstadoFase2(resFase2.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error cargando Google Business');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await marketingGbpAPI.sincronizar();
      const r = res.data;
      await cargar();
      alert(`Sync OK. ${r.nuevas_detectadas} reseña(s) nueva(s) detectada(s) · ${r.persistidas_total} persistidas en BD · ${r.user_rating_count} reseñas totales en Google`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error sincronizando');
    } finally {
      setSyncing(false);
    }
  };

  const reviewsFiltrados = useMemo(() => {
    if (!filtroRating) return reviews;
    return reviews.filter((r) => r.rating === Number(filtroRating));
  }, [reviews, filtroRating]);

  if (loading) {
    return (
      <div className="p-6 max-w-[1400px] mx-auto">
        <div className="text-center text-gray-500 py-12">
          <RefreshCw className="w-6 h-6 mx-auto animate-spin mb-2" />
          Cargando ficha de Google Business…
        </div>
      </div>
    );
  }

  // Estado: no configurado
  if (data && !data.configurado) {
    return (
      <div className="p-6 max-w-[1000px] mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Building2 className="w-7 h-7 text-blue-600" />
          <h1 className="text-2xl font-bold text-duetto-primary">Google Business</h1>
        </div>
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-5">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" />
            <h2 className="font-semibold text-amber-900">Falta configurar Places API</h2>
          </div>
          <pre className="text-xs bg-white border rounded p-3 whitespace-pre-wrap font-mono text-gray-800">
            {data.instrucciones}
          </pre>
          <div className="mt-4 text-sm text-gray-700">
            <p className="font-semibold mb-2">Paso a paso:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Entra en <a href="https://console.cloud.google.com/google/maps-apis/credentials" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Cloud Console → Maps Platform</a>.</li>
              <li>Habilita la <strong>Places API (New)</strong>.</li>
              <li>Crea una API key (puede restringirse a Places API).</li>
              <li>Añade a <code className="bg-gray-100 px-1 rounded">backend/.env</code>: <code className="bg-gray-100 px-1 rounded">GOOGLE_PLACES_API_KEY=...</code></li>
              <li>Reinicia el backend.</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  // Estado: configurado pero error
  if (data && !data.ok) {
    return (
      <div className="p-6 max-w-[1000px] mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Building2 className="w-7 h-7 text-blue-600" />
          <h1 className="text-2xl font-bold text-duetto-primary">Google Business</h1>
        </div>
        <div className="bg-red-50 border border-red-300 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-700 mt-0.5" />
            <div>
              <h2 className="font-semibold text-red-900 mb-1">Error con Places API</h2>
              <p className="text-sm text-red-800">{data.error}</p>
              <button onClick={cargar}
                className="mt-3 px-3 py-1.5 text-sm border border-red-300 rounded hover:bg-red-100">
                Reintentar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const f = data?.ficha || {};
  const kpis = data?.kpis_reviews || {};
  const horarios = f.regularOpeningHours?.weekdayDescriptions || [];
  const fotoPrincipal = f.photos?.[0]?.name; // Para construir URL de foto necesitamos llamar a otro endpoint

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Building2 className="w-7 h-7 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-duetto-primary">
              {f.displayName?.text || 'Google Business'}
            </h1>
            <div className="text-sm text-gray-500">
              {f.primaryTypeDisplayName?.text || f.primaryType || 'Negocio'} · {f.businessStatus || '—'}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={sync} disabled={syncing}
            className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-2">
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando…' : 'Sincronizar'}
          </button>
          {f.googleMapsUri && (
            <a href={f.googleMapsUri} target="_blank" rel="noopener noreferrer"
              className="px-3 py-2 text-sm bg-duetto-primary text-white rounded-md hover:opacity-90 inline-flex items-center gap-2">
              <ExternalLink className="w-4 h-4" /> Ver en Google Maps
            </a>
          )}
        </div>
      </div>

      {/* Tabs Fase 1 + Fase 2 */}
      <div className="flex gap-1 border-b border-gray-200 mb-5 overflow-x-auto">
        <TabButton activo={tabActiva === 'reviews'} onClick={() => setTabActiva('reviews')}>
          <MessageSquare className="w-4 h-4" /> Reseñas
          <span className="text-[10px] bg-gray-100 px-1 rounded ml-1">{reviews.length}</span>
        </TabButton>
        <TabButton activo={tabActiva === 'insights'} onClick={() => setTabActiva('insights')}>
          <TrendingUp className="w-4 h-4" /> Insights
          {estadoFase2 && !estadoFase2.configurado && (
            <span className="text-[10px] bg-amber-100 text-amber-700 px-1 rounded ml-1">Fase 2</span>
          )}
        </TabButton>
        <TabButton activo={tabActiva === 'posts'} onClick={() => setTabActiva('posts')}>
          <Edit className="w-4 h-4" /> Posts
          {estadoFase2 && !estadoFase2.configurado && (
            <span className="text-[10px] bg-amber-100 text-amber-700 px-1 rounded ml-1">Fase 2</span>
          )}
        </TabButton>
        <TabButton activo={tabActiva === 'qa'} onClick={() => setTabActiva('qa')}>
          <HelpCircle className="w-4 h-4" /> Q&A
          {estadoFase2 && !estadoFase2.configurado && (
            <span className="text-[10px] bg-amber-100 text-amber-700 px-1 rounded ml-1">Fase 2</span>
          )}
        </TabButton>
      </div>

      {tabActiva === 'insights' && <TabInsights estadoFase2={estadoFase2} />}
      {tabActiva === 'posts' && <TabPosts estadoFase2={estadoFase2} />}
      {tabActiva === 'qa' && <TabQa estadoFase2={estadoFase2} />}

      {tabActiva === 'reviews' && <>
      {/* KPI rating + total */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border rounded-lg p-4">
          <div className="text-[10px] uppercase font-semibold text-gray-500">Rating Google</div>
          <div className="mt-1">
            <StarsRating rating={f.rating} size="lg" />
          </div>
          <div className="text-xs text-gray-500 mt-1">{fmtNum(f.userRatingCount)} reseñas totales en Google</div>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <div className="text-[10px] uppercase font-semibold text-gray-500">Persistidas en BD</div>
          <div className="text-2xl font-bold mt-1">{fmtNum(kpis.total)}</div>
          <div className="text-xs text-gray-500 mt-1">Rating medio: <strong>{kpis.rating_medio?.toFixed(2) || '—'}</strong></div>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <div className="text-[10px] uppercase font-semibold text-gray-500">Últimas 30 días</div>
          <div className="text-2xl font-bold mt-1 text-emerald-700">{fmtNum(kpis.ultimas_30d)}</div>
          <div className="text-xs text-gray-500 mt-1">Reseñas recientes</div>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <div className="text-[10px] uppercase font-semibold text-gray-500">Últimas 90 días</div>
          <div className="text-2xl font-bold mt-1">{fmtNum(kpis.ultimas_90d)}</div>
          <div className="text-xs text-gray-500 mt-1">
            <TrendingUp className="w-3 h-3 inline" /> Frecuencia
          </div>
        </div>
      </div>

      {/* Ficha datos + distribución */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border rounded-lg p-4 lg:col-span-2">
          <h3 className="text-sm font-semibold mb-3">Ficha pública</h3>
          <div className="space-y-2 text-sm">
            {f.formattedAddress && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                <span>{f.formattedAddress}</span>
              </div>
            )}
            {f.internationalPhoneNumber && (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" />
                <a href={`tel:${f.internationalPhoneNumber}`} className="text-blue-600 hover:underline">
                  {f.internationalPhoneNumber}
                </a>
              </div>
            )}
            {f.websiteUri && (
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-gray-400" />
                <a href={f.websiteUri} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  {f.websiteUri}
                </a>
              </div>
            )}
            {horarios.length > 0 && (
              <div className="flex items-start gap-2 mt-2">
                <Clock className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                <div className="text-xs space-y-0.5">
                  {horarios.map((d, i) => <div key={i}>{d}</div>)}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <h3 className="text-sm font-semibold mb-3">Distribución de reseñas</h3>
          {kpis.total > 0 ? (
            <DistribucionRating distribucion={kpis.distribucion} total={kpis.total} />
          ) : (
            <div className="text-xs text-gray-400">Sin reseñas persistidas aún. Pulsa "Sincronizar".</div>
          )}
        </div>
      </div>

      {/* Lista de reviews */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold">Reseñas persistidas ({reviews.length})</h3>
            <span className="text-xs text-gray-500">
              · Google muestra solo 5 vía Places API. Las acumulamos en BD cada sync.
            </span>
          </div>
          <div className="flex items-center gap-2">
            <select value={filtroRating} onChange={(e) => setFiltroRating(e.target.value)}
              className="px-2 py-1 text-xs border rounded-md">
              <option value="">Todas las estrellas</option>
              {[5, 4, 3, 2, 1].map((s) => (
                <option key={s} value={s}>{s}★ ({(kpis.distribucion || {})[s] || 0})</option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {reviewsFiltrados.map((r) => (
            <ReviewCard key={r.review_id} r={r} onSugerirRespuesta={(rev) => setIaReview(rev)} />
          ))}
        </div>
        {reviewsFiltrados.length === 0 && (
          <div className="text-center text-gray-400 py-8 text-sm">
            {filtroRating ? 'Sin reseñas con esa puntuación.' : 'Sin reseñas. Pulsa "Sincronizar" para traerlas.'}
          </div>
        )}
      </div>
      </>}

      <SugerenciaRespuestaDialog
        open={!!iaReview}
        review={iaReview}
        onClose={() => setIaReview(null)}
      />

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg text-sm">
          {error}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Tab buttons + componentes Fase 2
// ──────────────────────────────────────────────────────────────────────────

function TabButton({ activo, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-1.5 shrink-0 ${
        activo
          ? 'border-blue-600 text-blue-700'
          : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
      }`}
    >
      {children}
    </button>
  );
}

function PlaceholderFase2({ titulo, descripcion, instrucciones }) {
  return (
    <div className="bg-amber-50 border border-amber-300 rounded-lg p-5">
      <div className="flex items-start gap-3 mb-3">
        <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" />
        <div>
          <h2 className="font-semibold text-amber-900">{titulo}</h2>
          <p className="text-sm text-amber-800 mt-1">{descripcion}</p>
        </div>
      </div>
      {instrucciones && (
        <pre className="text-xs bg-white border rounded p-3 whitespace-pre-wrap font-mono text-gray-800">
          {instrucciones}
        </pre>
      )}
      <a
        href="https://support.google.com/business/contact/api_default"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 mt-3 text-sm text-amber-900 hover:underline"
      >
        <ExternalLink className="w-4 h-4" /> Solicitar acceso a Business Profile API
      </a>
    </div>
  );
}

function TabInsights({ estadoFase2 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!estadoFase2?.configurado) {
      setLoading(false);
      return;
    }
    marketingGbpAPI.insightsFase2(30)
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
  }, [estadoFase2]);

  if (!estadoFase2?.configurado) {
    return (
      <PlaceholderFase2
        titulo="Insights pendientes de Business Profile API"
        descripcion="Cuando Google apruebe el acceso a la API verás impresiones (búsqueda vs Maps), llamadas, peticiones de ruta y clicks a la web."
        instrucciones={estadoFase2?.instrucciones}
      />
    );
  }
  if (loading) return <div className="text-center text-gray-500 py-12">Cargando insights…</div>;
  if (!data?.serie?.length) {
    return <div className="text-center text-gray-500 py-12">Sin datos todavía. El cron diario sincronizará a las 06:30 Madrid.</div>;
  }
  const t = data.totales || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiInsight label="Impresiones totales" value={t.impresiones_total} icon={Eye} />
        <KpiInsight label="Acciones totales" value={t.acciones_total} icon={MousePointerClick} />
        <KpiInsight label="Llamadas" value={t.llamadas} icon={Phone} />
        <KpiInsight label="Direcciones" value={t.direcciones} icon={Navigation} />
        <KpiInsight label="Clicks a web" value={t.clicks_web} icon={Globe} />
      </div>
      <div className="bg-white border rounded-lg p-4">
        <h3 className="text-sm font-semibold mb-3">Detalle por canal (últimos {data.dias} días)</h3>
        <table className="w-full text-xs">
          <thead className="text-gray-500 border-b">
            <tr>
              <th className="text-left py-1">Métrica</th>
              <th className="text-right py-1">Total {data.dias}d</th>
              <th className="text-right py-1">Media diaria</th>
            </tr>
          </thead>
          <tbody>
            <Row label="Imp. Búsqueda Desktop" v={t.impresiones_search_desktop} d={data.dias} />
            <Row label="Imp. Búsqueda Mobile" v={t.impresiones_search_mobile} d={data.dias} />
            <Row label="Imp. Maps Desktop" v={t.impresiones_maps_desktop} d={data.dias} />
            <Row label="Imp. Maps Mobile" v={t.impresiones_maps_mobile} d={data.dias} />
            <Row label="Llamadas" v={t.llamadas} d={data.dias} />
            <Row label="Peticiones de ruta" v={t.direcciones} d={data.dias} />
            <Row label="Clicks a la web" v={t.clicks_web} d={data.dias} />
            <Row label="Conversaciones (mensajes)" v={t.conversaciones} d={data.dias} />
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpiInsight({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border rounded-lg p-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase font-semibold text-gray-500">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5 text-gray-400" />}
      </div>
      <div className="text-xl font-bold text-gray-900">{(value ?? 0).toLocaleString('es-ES')}</div>
    </div>
  );
}

function Row({ label, v, d }) {
  const media = d > 0 ? Math.round((v || 0) / d) : 0;
  return (
    <tr className="border-b last:border-0">
      <td className="py-1">{label}</td>
      <td className="text-right py-1 font-medium">{(v || 0).toLocaleString('es-ES')}</td>
      <td className="text-right py-1 text-gray-500">{media.toLocaleString('es-ES')}/día</td>
    </tr>
  );
}

function TabPosts({ estadoFase2 }) {
  if (!estadoFase2?.configurado) {
    return (
      <PlaceholderFase2
        titulo="Posts de Business Profile pendientes"
        descripcion="Cuando Google apruebe podrás crear posts (novedades, eventos, ofertas) directamente desde el ERP. Aparecerán en tu ficha de Google Maps."
        instrucciones={estadoFase2?.instrucciones}
      />
    );
  }
  return (
    <div className="text-center text-gray-500 py-12">
      <Edit className="w-8 h-8 mx-auto mb-2" />
      <p className="font-semibold text-gray-700">Listo para publicar posts</p>
      <p className="text-sm mt-1">La interfaz de creación se activará la primera vez que entres aquí con Fase 2 activa.</p>
    </div>
  );
}

function TabQa({ estadoFase2 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    marketingGbpAPI.listarQa()
      .then((res) => setItems(res.data?.items || []))
      .finally(() => setLoading(false));
  }, []);

  if (!estadoFase2?.configurado) {
    return (
      <PlaceholderFase2
        titulo="Q&A pendiente de Business Profile API"
        descripcion="Cuando Google apruebe verás las preguntas que hacen los clientes en tu ficha y podrás responder como propietario (las respuestas del propietario son las que ranquean en Google)."
        instrucciones={estadoFase2?.instrucciones}
      />
    );
  }
  if (loading) return <div className="text-center text-gray-500 py-12">Cargando…</div>;
  if (!items.length) {
    return <div className="text-center text-gray-500 py-12">Sin preguntas todavía.</div>;
  }
  return (
    <div className="space-y-3">
      {items.map((q) => (
        <div key={q.question_id} className="bg-white border rounded-lg p-4">
          <div className="flex items-start gap-2 mb-2">
            <HelpCircle className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-medium text-sm">{q.texto_pregunta}</div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                {q.autor || 'Anónimo'} · {q.upvotes} 👍
              </div>
            </div>
          </div>
          {q.respuesta_propietario ? (
            <div className="ml-7 bg-blue-50 border border-blue-200 rounded p-2.5 text-sm">
              <div className="text-[10px] uppercase font-semibold text-blue-700 mb-1">Respuesta de Duetto Catering</div>
              {q.respuesta_propietario}
            </div>
          ) : (
            <div className="ml-7 text-xs text-amber-700 italic">⚠ Sin respuesta del propietario. Responde desde el panel de Google.</div>
          )}
        </div>
      ))}
    </div>
  );
}
